import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, Globe, Heart, TrendingUp, Sparkles, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const About = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const stats = [
    { number: '5+', label: t('about.stats.restaurants', 'Restaurants Served'), icon: Users },
    { number: '2+', label: t('about.stats.countries', 'Countries'), icon: Globe },
    { number: '98%', label: t('about.stats.customerSatisfaction', 'Customer Satisfaction'), icon: Heart },
    { number: '1000+', label: t('about.stats.ordersProcessed', 'Orders Processed'), icon: TrendingUp }
  ];

  const values = [
    {
      icon: Target,
      title: t('about.values.mission.title', 'Our Mission'),
      description: t('about.values.mission.description', 'To empower restaurants of all sizes with technology that enhances customer experience and streamlines operations.'),
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Users,
      title: t('about.values.vision.title', 'Our Vision'),
      description: t('about.values.vision.description', 'A world where every restaurant can focus on what they do best - creating amazing food and memorable experiences.'),
      gradient: 'from-purple-500 to-violet-500'
    },
    {
      icon: Heart,
      title: t('about.values.values.title', 'Our Values'),
      description: t('about.values.values.description', 'We believe in innovation, reliability, and building lasting partnerships with our restaurant community.'),
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  const team = [
    {
      name: 'Aboubakr Taouil',
      role: t('about.team.cto', 'CTO'),
      image: '👨🏻‍💻',
      bio: t('about.team.ctoBio', 'Tech visionary with background in scalable SaaS platforms')
    },
    {
      name: t('about.team.ceoNames', 'Tassilo & Eliot'),
      role: t('about.team.ceoRole', 'CEOs & Founders'),
      image: '👨🏻‍💼',
      bio: t('about.team.ceoBio', 'Product leader passionate about user experience design')
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_100%_200px,#f0f9ff,transparent)]"></div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28">
        <div className="container relative">
          <div className={`text-center max-w-4xl mx-auto transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Sparkles className="h-4 w-4" />
              {t('about.hero.badge', 'Our Story')}
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('about.hero.title.part1', 'Revolutionizing')}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {t('about.hero.title.part2', ' Restaurant Technology')}
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {t('about.hero.subtitle', "We're on a mission to transform how restaurants operate, serving over 10,000 establishments worldwide with our innovative SaaS platform.")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center"
              >
                {t('about.hero.cta.join', 'Join Our Story')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20">
        <div className="container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={stat.label}
                  className={`text-center transition-all duration-500 ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl text-white mb-4">
                    <Icon className="h-8 w-8" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('about.missionValues.title', 'Our Mission & Values')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('about.missionValues.subtitle', 'Driving innovation in restaurant technology with purpose and passion')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={value.title}
                  className={`bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${value.gradient} text-white mb-6`}>
                    <Icon className="h-8 w-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('about.team.title', 'Meet Our Team')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('about.team.subtitle', 'Passionate individuals dedicated to transforming restaurant technology')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {team.map((member, index) => (
              <div
                key={member.name}
                className={`bg-white rounded-3xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="text-4xl mb-4">{member.image}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <div className="text-blue-600 font-semibold mb-3">{member.role}</div>
                <p className="text-gray-600 text-sm">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
        <div className="container text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('about.cta.title', 'Ready to Transform Your Restaurant?')}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {t('about.cta.subtitle', 'Join thousands of successful restaurants using our platform to streamline operations and enhance customer experience.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-white text-blue-600 px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                {t('navigation.register', 'Get Started Free')}
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-3 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                {t('navigation.contact', 'Contact')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;